import { c as _c } from "react/compiler-runtime";
/**
 * Hook for subscribing to Supabase Realtime broadcasts for timestamp updates.
 * Handles both global topics (networks list, optimization) and network-specific topics.
 */

import { useEffect } from 'react';
import { REALTIME_SUBSCRIBE_STATES, RealtimeChannel } from '@supabase/supabase-js';
import { createClient } from '@/utils/supabase/client';
import { createBroadcastHandler } from './broadcastHandlers';
import { GLOBAL_TOPICS, NETWORK_SPECIFIC_TOPICS, TopicName } from './realtimeConstants';
import { requestRealtimeResync } from './realtimeResync';
const supabase = createClient();
const channels = new Map<TopicName, RealtimeChannel>();
let globalChannelsInitialized = false;
let networkChannelsInitialized = false;
let authListenerInstalled = false;

// Tracks whether the hook has asked for network-specific channels. Stored at
// module level so the auth listener can re-subscribe after a teardown
// triggered by SIGNED_IN / TOKEN_REFRESHED without needing access to a hook
// ref.
let networkChannelsRequested = false;
const sharedHandler = createBroadcastHandler();

// The Supabase Realtime tenant cold-stops when idle and re-inits on the next
// connection. A `.subscribe()` landing in that window transiently gets
// CHANNEL_ERROR / TIMED_OUT, after which supabase-js auto-reconnects and
// succeeds. Those transient states are benign, so only escalate to
// console.error (captured by PostHog as an exception) once a channel has
// failed this many times in a row without an intervening SUBSCRIBED.
const SUSTAINED_FAILURE_THRESHOLD = 5;

// Consecutive non-SUBSCRIBED subscribe callbacks per channel. Reset to 0 on
// SUBSCRIBED. Escalates warn → error once the threshold is crossed.
const consecutiveFailures = new Map<TopicName, number>();
function subscribeChannel(topic: TopicName): RealtimeChannel {
  return supabase.channel(topic, {
    config: {
      private: false
    }
  }).on('broadcast', {
    event: 'INSERT'
  }, sharedHandler).on('broadcast', {
    event: 'UPDATE'
  }, sharedHandler).on('broadcast', {
    event: 'DELETE'
  }, sharedHandler).subscribe((status, err) => {
    if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
      consecutiveFailures.set(topic, 0);
      // Anything published before this point was dropped, not queued, so
      // reconcile against the server instead of assuming the cache is
      // current. See realtimeResync.ts for the windows this covers.
      requestRealtimeResync();
      return;
    }
    if (status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
      console.warn(`Realtime channel "${topic}" closed`);
      return;
    }
    // CHANNEL_ERROR / TIMED_OUT: retryable and usually self-healing. Warn
    // (not captured by PostHog) until a channel has failed persistently,
    // then escalate to error so a genuine outage still surfaces.
    const failures = (consecutiveFailures.get(topic) ?? 0) + 1;
    consecutiveFailures.set(topic, failures);
    const message = `Realtime channel "${topic}" did not subscribe (status=${status}, attempt=${failures})`;
    if (failures >= SUSTAINED_FAILURE_THRESHOLD) {
      console.error(message, err);
    } else {
      console.warn(message, err);
    }
  });
}
function initializeGlobalChannels(): void {
  if (globalChannelsInitialized) return;
  globalChannelsInitialized = true;
  for (const topic of GLOBAL_TOPICS) {
    channels.set(topic, subscribeChannel(topic));
  }
}
function initializeNetworkChannels(): void {
  if (networkChannelsInitialized) return;
  networkChannelsInitialized = true;
  for (const topic of NETWORK_SPECIFIC_TOPICS) {
    channels.set(topic, subscribeChannel(topic));
  }
}
function teardownAllChannels(): void {
  for (const channel of channels.values()) {
    void supabase.removeChannel(channel);
  }
  channels.clear();
  consecutiveFailures.clear();
  globalChannelsInitialized = false;
  networkChannelsInitialized = false;
}
function installAuthListener(): void {
  if (authListenerInstalled) return;
  authListenerInstalled = true;
  // Realtime auth is tied to the session. When the user signs out / in or
  // the token is refreshed, tear down the singleton channels so they can
  // be re-subscribed under the new session. Otherwise channels hold a
  // stale JWT and silently stop receiving messages.
  supabase.auth.onAuthStateChange(event => {
    if (event === 'SIGNED_OUT') {
      teardownAllChannels();
      return;
    }
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      teardownAllChannels();
      initializeGlobalChannels();
      if (networkChannelsRequested) {
        initializeNetworkChannels();
      }
    }
  });
}
type UseRealtimeTimestampsProps = {
  networkId: string | undefined;
  enabled: boolean;
};
export function useRealtimeTimestamps(t0) {
  const $ = _c(7);
  const {
    networkId,
    enabled
  } = t0;
  let t1;
  let t2;
  if ($[0] !== enabled) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      installAuthListener();
      initializeGlobalChannels();
    };
    t2 = [enabled];
    $[0] = enabled;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== enabled || $[4] !== networkId) {
    t3 = () => {
      if (!enabled || !networkId) {
        return;
      }
      networkChannelsRequested = true;
      initializeNetworkChannels();
    };
    t4 = [enabled, networkId];
    $[3] = enabled;
    $[4] = networkId;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
}